import React, { useEffect, useState } from 'react';
import type { User, PathwayBlock, AssessmentAttempt, AssessmentQuestion } from '../../../types';
import { DataService } from '../../../services/firebase';
import { useTranslation } from 'react-i18next';
import { FileQuestion, CheckCircle, XCircle, Clock, RotateCcw } from 'lucide-react';

interface AssessmentBlockViewProps {
  user: User;
  block: PathwayBlock;
  pathwayId: string;
}

type ViewState = 'overview' | 'taking' | 'result';

export const AssessmentBlockView: React.FC<AssessmentBlockViewProps> = ({ user, block, pathwayId }) => {
  const { t } = useTranslation();
  const [attempts, setAttempts] = useState<AssessmentAttempt[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewState, setViewState] = useState<ViewState>('overview');
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const config = block.assessmentConfig;
  const questions = config?.questions || [];
  const maxAttempts = config?.maxAttempts || 0; // 0 = unlimited
  const passingThreshold = config?.passingThreshold || 70;

  useEffect(() => {
    const load = async () => {
      try {
        const data = await DataService.getAssessmentAttempts(user.id, pathwayId, block.id);
        setAttempts(data);

        const passed = data.find(a => a.passed === true);
        if (passed) {
          await DataService.setBlockProgress({
            candidateId: user.id,
            pathwayId,
            blockId: block.id,
            status: 'complete',
            completedAt: passed.gradedAt || passed.submittedAt,
          });
        }
      } catch (e) {
        console.error('Failed to load assessment attempts', e);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [user.id, block.id, pathwayId]);

  const latestAttempt = attempts.length > 0 ? attempts[attempts.length - 1] : null;
  const hasPassed = attempts.some(a => a.passed === true);
  const hasPendingReview = attempts.some(a => a.status === 'pending_review');
  const attemptsUsed = attempts.length;
  const canRetry = !hasPassed && !hasPendingReview && (maxAttempts === 0 || attemptsUsed < maxAttempts);

  const handleStartAssessment = () => {
    setAnswers({});
    setViewState('taking');
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      // Auto-grade MC and T/F questions
      const graded = questions.map((q: AssessmentQuestion) => {
        const answer = answers[q.id] || '';
        let isCorrect: boolean | undefined;
        if (q.type === 'multiple_choice' || q.type === 'true_false') {
          isCorrect = answer === q.correctAnswer;
        }
        // short_answer → undefined (needs manual grading)
        return { questionId: q.id, answer, isCorrect };
      });

      const autoGradable = graded.filter(a => a.isCorrect !== undefined);
      const correctCount = autoGradable.filter(a => a.isCorrect).length;
      const hasManualQuestions = graded.some(a => a.isCorrect === undefined);

      let score: number | undefined;
      let passed: boolean | undefined;
      let status: 'submitted' | 'graded' | 'pending_review' = 'graded';

      if (hasManualQuestions) {
        status = 'pending_review';
      } else {
        score = questions.length > 0 ? Math.round((correctCount / questions.length) * 100) : 0;
        passed = score >= passingThreshold;
      }

      const attempt: Omit<AssessmentAttempt, 'id'> = {
        candidateId: user.id,
        pathwayId,
        blockId: block.id,
        attemptNumber: attemptsUsed + 1,
        answers: graded,
        score,
        passed,
        status,
        submittedAt: Date.now(),
        gradedAt: status === 'graded' ? Date.now() : undefined,
      };

      const saved = await DataService.saveAssessmentAttempt(attempt);
      setAttempts(prev => [...prev, saved]);

      if (passed) {
        await DataService.setBlockProgress({
          candidateId: user.id,
          pathwayId,
          blockId: block.id,
          status: 'complete',
          completedAt: Date.now(),
        });
      }

      setViewState('result');
    } catch (e) {
      console.error('Failed to submit assessment', e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const allAnswered = questions.every((q: AssessmentQuestion) => (answers[q.id] || '').trim().length > 0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-aero-600"></div>
      </div>
    );
  }

  // Taking the assessment
  if (viewState === 'taking') {
    return (
      <div className="p-4 max-w-2xl mx-auto space-y-4">
        <div className="bg-white rounded-xl border border-aero-200 p-5">
          <h2 className="text-lg font-bold text-aero-900">{block.name}</h2>
          <p className="text-xs text-aero-500 mt-1">
            {t('candidate.block.assessment.attemptLabel', { n: attemptsUsed + 1 })}
            {maxAttempts > 0 && ` ${t('candidate.block.assessment.of')} ${maxAttempts}`}
          </p>
        </div>

        {questions.map((q: AssessmentQuestion, idx: number) => (
          <div key={q.id} className="bg-white rounded-xl border border-aero-200 p-4">
            <p className="text-sm font-semibold text-aero-900 mb-3">
              <span className="text-aero-400 mr-1">{idx + 1}.</span> {q.text}
            </p>

            {q.type === 'multiple_choice' && q.options && (
              <div className="space-y-2">
                {q.options.map((opt: string) => (
                  <label key={opt} className="flex items-center gap-3 p-2.5 rounded-lg border border-aero-100 hover:bg-aero-50 cursor-pointer">
                    <input
                      type="radio"
                      name={q.id}
                      value={opt}
                      checked={answers[q.id] === opt}
                      onChange={() => setAnswers(prev => ({ ...prev, [q.id]: opt }))}
                      className="w-4 h-4 text-aero-600"
                    />
                    <span className="text-sm text-aero-800">{opt}</span>
                  </label>
                ))}
              </div>
            )}

            {q.type === 'true_false' && (
              <div className="space-y-2">
                {['True', 'False'].map((opt) => (
                  <label key={opt} className="flex items-center gap-3 p-2.5 rounded-lg border border-aero-100 hover:bg-aero-50 cursor-pointer">
                    <input
                      type="radio"
                      name={q.id}
                      value={opt}
                      checked={answers[q.id] === opt}
                      onChange={() => setAnswers(prev => ({ ...prev, [q.id]: opt }))}
                      className="w-4 h-4 text-aero-600"
                    />
                    <span className="text-sm text-aero-800">{opt}</span>
                  </label>
                ))}
              </div>
            )}

            {q.type === 'short_answer' && (
              <textarea
                value={answers[q.id] || ''}
                onChange={(e) => setAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                rows={3}
                className="w-full border border-aero-200 rounded-lg p-2.5 text-sm text-aero-800 focus:outline-none focus:ring-2 focus:ring-aero-400"
                placeholder={t('candidate.block.assessment.typeAnswer')}
              />
            )}
          </div>
        ))}

        <button
          onClick={handleSubmit}
          disabled={!allAnswered || isSubmitting}
          className="w-full py-3.5 bg-aero-600 hover:bg-aero-700 disabled:bg-aero-300 text-white rounded-xl font-semibold text-sm transition-colors"
        >
          {isSubmitting ? t('common.submitting') : t('candidate.block.assessment.submit')}
        </button>
      </div>
    );
  }

  // Result view (after submission)
  if (viewState === 'result' && latestAttempt) {
    return (
      <div className="p-4 max-w-2xl mx-auto space-y-4">
        <div className="bg-white rounded-xl border border-aero-200 p-5">
          <h2 className="text-lg font-bold text-aero-900">{block.name}</h2>
        </div>

        {latestAttempt.status === 'pending_review' ? (
          <div className="bg-yellow-50 rounded-xl border-2 border-yellow-200 p-5 flex items-center gap-3">
            <Clock className="w-5 h-5 text-yellow-500" />
            <div>
              <p className="text-sm font-semibold text-yellow-700">{t('candidate.block.assessment.pendingReview')}</p>
              <p className="text-xs text-yellow-600 mt-0.5">{t('candidate.block.assessment.pendingReviewDesc')}</p>
            </div>
          </div>
        ) : latestAttempt.passed ? (
          <div className="bg-green-50 rounded-xl border-2 border-green-200 p-5 flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <div>
              <p className="text-sm font-semibold text-green-700">{t('candidate.block.assessment.passed')}</p>
              <p className="text-xs text-green-600 mt-0.5">
                {t('candidate.block.assessment.scoreLabel', { score: latestAttempt.score, threshold: passingThreshold })}
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-red-50 rounded-xl border-2 border-red-200 p-5">
            <div className="flex items-center gap-3">
              <XCircle className="w-5 h-5 text-red-500" />
              <div>
                <p className="text-sm font-semibold text-red-700">{t('candidate.block.assessment.failed')}</p>
                <p className="text-xs text-red-600 mt-0.5">
                  {t('candidate.block.assessment.scoreLabel', { score: latestAttempt.score, threshold: passingThreshold })}
                </p>
              </div>
            </div>
            {canRetry && (
              <button
                onClick={handleStartAssessment}
                className="mt-3 w-full flex items-center justify-center gap-2 py-3 bg-red-500 hover:bg-red-600 text-white rounded-xl font-semibold text-sm transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                {t('candidate.block.assessment.retry')}
              </button>
            )}
          </div>
        )}

        <button
          onClick={() => setViewState('overview')}
          className="w-full py-3 border border-aero-200 text-aero-700 rounded-xl font-semibold text-sm hover:bg-aero-50 transition-colors"
        >
          {t('candidate.block.assessment.backToOverview')}
        </button>
      </div>
    );
  }

  // Overview (default)
  return (
    <div className="p-4 max-w-2xl mx-auto space-y-4">
      {/* Block header */}
      <div className="bg-white rounded-xl border border-aero-200 p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-aero-100 rounded-xl flex items-center justify-center flex-shrink-0">
            <FileQuestion className="w-5 h-5 text-aero-600" />
          </div>
          <div>
            <p className="text-xs font-bold text-aero-400 uppercase tracking-wide mb-1">{t('candidate.block.assessment.title')}</p>
            <h2 className="text-lg font-bold text-aero-900">{block.name}</h2>
            {block.description && <p className="text-sm text-aero-600 mt-1">{block.description}</p>}
          </div>
        </div>
      </div>

      {/* Status */}
      {hasPassed ? (
        <div className="bg-green-50 rounded-xl border-2 border-green-200 p-5 flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-green-500" />
          <div>
            <p className="text-sm font-semibold text-green-700">{t('candidate.block.assessment.passed')}</p>
            <p className="text-xs text-green-600 mt-0.5">
              {t('candidate.block.assessment.scoreLabel', {
                score: attempts.find(a => a.passed)?.score,
                threshold: passingThreshold,
              })}
            </p>
          </div>
        </div>
      ) : hasPendingReview ? (
        <div className="bg-yellow-50 rounded-xl border-2 border-yellow-200 p-5 flex items-center gap-3">
          <Clock className="w-5 h-5 text-yellow-500" />
          <div>
            <p className="text-sm font-semibold text-yellow-700">{t('candidate.block.assessment.pendingReview')}</p>
            <p className="text-xs text-yellow-600 mt-0.5">{t('candidate.block.assessment.pendingReviewDesc')}</p>
          </div>
        </div>
      ) : (
        <div className="bg-aero-50 rounded-xl border border-aero-200 p-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-semibold text-aero-600">{t('candidate.block.assessment.questions')}</span>
            <span className="text-xs text-aero-500">{questions.length}</span>
          </div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-semibold text-aero-600">{t('candidate.block.assessment.passingScore')}</span>
            <span className="text-xs text-aero-500">{passingThreshold}%</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-aero-600">{t('candidate.block.assessment.attempts')}</span>
            <span className="text-xs text-aero-500">
              {attemptsUsed}{maxAttempts > 0 ? ` / ${maxAttempts}` : ''}
            </span>
          </div>
        </div>
      )}

      {/* Start / Retry button */}
      {canRetry && (
        <button
          onClick={handleStartAssessment}
          className="w-full flex items-center justify-center gap-2 py-3.5 bg-aero-600 hover:bg-aero-700 text-white rounded-xl font-semibold text-sm transition-colors shadow-sm"
        >
          {attemptsUsed === 0
            ? t('candidate.block.assessment.start')
            : <><RotateCcw className="w-4 h-4" /> {t('candidate.block.assessment.retry')}</>
          }
        </button>
      )}

      {/* Previous attempts */}
      {attempts.length > 0 && (
        <div>
          <h3 className="text-sm font-bold text-aero-700 mb-2">{t('candidate.block.assessment.previousAttempts')}</h3>
          <div className="space-y-2">
            {attempts.map((a) => (
              <div key={a.id} className="bg-white rounded-xl border border-aero-200 p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-aero-900">
                    {t('candidate.block.assessment.attemptLabel', { n: a.attemptNumber })}
                  </p>
                  <p className="text-xs text-aero-500 mt-0.5">{new Date(a.submittedAt).toLocaleDateString()}</p>
                </div>
                <div className="text-right">
                  {a.status === 'pending_review' ? (
                    <span className="text-xs font-semibold text-yellow-600 bg-yellow-100 px-2 py-0.5 rounded-full">{t('candidate.block.assessment.pendingReview')}</span>
                  ) : a.passed ? (
                    <span className="text-xs font-semibold text-green-600 bg-green-100 px-2 py-0.5 rounded-full">{a.score}% ✓</span>
                  ) : (
                    <span className="text-xs font-semibold text-red-600 bg-red-100 px-2 py-0.5 rounded-full">{a.score}% ✗</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
